// AccountPage — /portal/account, the avatar menu's "Account" (ticket 196). Your
// own profile (name, phone, NMLS ID; email read-only), change password,
// two-step sign-in status with a reset, language, "Sign out my other devices"
// and "Replay the tour".
import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { api, changePassword, signOutOtherDevices } from './api';
import { MIN_PASSWORD } from './passwordPolicy';
import { errorMessage } from './errors';
import { useAuth } from './AuthContext';
import { useLang } from './LanguageContext';
import { LANGS } from './OnboardingTour';
import PageContainer from './kit/PageContainer';
import { Input } from '../components/ui/input';
import PhoneInput from './shared/PhoneInput';
import PasswordInput from './shared/PasswordInput';
import { phoneDigits } from './shared/phone';

const CARD = 'bg-white border border-slate-200 rounded-xl p-5 space-y-3 max-w-xl';
const SECTION = 'text-[11px] font-bold uppercase tracking-wide text-slate-500';
const ACTION = 'text-[13px] font-semibold px-4 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-50';

const EMPTY_PW = { current: '', next: '', confirm: '' };

function ProfileCard({ user, onSaved }) {
  const [form, setForm] = useState({ name: '', phone: '', nmls: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Fill (and refill after a save) from the signed-in user.
  useEffect(() => {
    if (!user) return;
    setForm({ name: user.name || '', phone: phoneDigits(user.phone || ''), nmls: user.nmls || '' });
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const dirty = !!user && (
    form.name.trim() !== (user.name || '')
    || form.phone !== phoneDigits(user.phone || '')
    || form.nmls.trim() !== (user.nmls || '')
  );

  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.name.trim()) return setError('Name cannot be empty.');
    setError('');
    setBusy(true);
    try {
      // Only what changed: a field the admin stored in another format (say
      // "NMLS #123") must not block saving a different one.
      const body = {};
      if (form.name.trim() !== (user.name || '')) body.name = form.name.trim();
      if (form.phone !== phoneDigits(user.phone || '')) body.phone = form.phone;
      if (form.nmls.trim() !== (user.nmls || '')) body.nmls = form.nmls.trim();
      const { data } = await api.patch('/auth/profile', body);
      toast({ variant: 'success', title: 'Profile saved' });
      onSaved(data);
    } catch (err) {
      setError(errorMessage(err, 'Could not save your profile'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className={CARD} onSubmit={save} data-testid="account-profile-form">
      <div className={SECTION}>Profile</div>
      <label className="block space-y-1">
        <span className="text-[12px] text-slate-600">Name</span>
        <Input value={form.name} onChange={set('name')} maxLength={200} autoComplete="name" data-testid="account-name" />
      </label>
      <label className="block space-y-1">
        <span className="text-[12px] text-slate-600">Email</span>
        <Input value={user?.email || ''} disabled readOnly data-testid="account-email" />
        <span className="block text-[11px] text-slate-400">This is how you sign in, so it cannot be changed here.</span>
      </label>
      <label className="block space-y-1">
        <span className="text-[12px] text-slate-600">Phone</span>
        <PhoneInput value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} autoComplete="tel" data-testid="account-phone" />
      </label>
      <label className="block space-y-1">
        <span className="text-[12px] text-slate-600">NMLS ID</span>
        <Input value={form.nmls} onChange={set('nmls')} inputMode="numeric" maxLength={10} data-testid="account-nmls" />
      </label>
      {error && <p role="alert" className="text-[12px] text-red-600" data-testid="account-profile-error">{error}</p>}
      <button type="submit" disabled={busy || !dirty} data-testid="account-profile-save" className={ACTION}>
        {busy && <Loader2 size={13} className="animate-spin" />}
        Save profile
      </button>
    </form>
  );
}

function TwoStepCard() {
  const [status, setStatus] = useState(null);
  const [failed, setFailed] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/mfa/status');
      setStatus(data);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reset = async (e) => {
    e.preventDefault();
    if (busy || !code.trim()) return;
    setBusy(true);
    setError('');
    try {
      await api.post('/auth/mfa/reset-own', { code: code.trim() });
      toast({ variant: 'success', title: 'Two-step sign-in reset', description: 'You will set it up again the next time you sign in.' });
      setResetting(false);
      setCode('');
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not reset two-step sign-in'));
    } finally {
      setBusy(false);
    }
  };

  let summary = 'Checking…';
  if (failed) summary = 'Could not load your two-step sign-in status.';
  else if (status?.enrolled) {
    const left = status.recovery_codes_remaining;
    summary = `On. ${left} recovery ${left === 1 ? 'code' : 'codes'} left.`;
  } else if (status?.required) summary = 'Required for your seat, not set up yet. You will be asked to set it up the next time you sign in.';
  else if (status) summary = 'Not required for your seat.';

  return (
    <div className={CARD} data-testid="account-twostep">
      <div className={SECTION}>Two-step sign-in</div>
      <p className="text-[13px] text-slate-700" data-testid="account-twostep-status">{summary}</p>
      {status?.enrolled && !resetting && (
        <button type="button" onClick={() => setResetting(true)} data-testid="account-twostep-reset" className={ACTION}>
          Reset two-step sign-in
        </button>
      )}
      {status?.enrolled && resetting && (
        <form className="space-y-1.5" onSubmit={reset} data-testid="account-twostep-form">
          <p className="text-[12px] text-slate-500">
            Enter a code from your authenticator app, or an unused recovery code. This removes your current
            authenticator{status.required ? '; you will set up a new one the next time you sign in' : ''}.
          </p>
          <Input value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" placeholder="Code"
            aria-label="Authenticator or recovery code" data-testid="account-twostep-code" />
          {error && <p role="alert" className="text-[12px] text-red-600" data-testid="account-twostep-error">{error}</p>}
          <div className="flex gap-2">
            <button type="submit" disabled={busy || !code.trim()} data-testid="account-twostep-confirm" className={ACTION}>
              {busy && <Loader2 size={13} className="animate-spin" />}
              Reset
            </button>
            <button type="button" onClick={() => { setResetting(false); setCode(''); setError(''); }} className={ACTION}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}

export default function AccountPage() {
  const { user, updateUser } = useAuth();
  const { lang, setLang } = useLang();
  // The layout owns the tour; it hands the page a way to open it.
  const outlet = useOutletContext();
  // 'password' | 'others' | 'tour' while that request is in flight.
  const [busy, setBusy] = useState(null);
  const [pw, setPw] = useState(EMPTY_PW);
  const [pwError, setPwError] = useState('');
  const setPwField = (k) => (e) => setPw((p) => ({ ...p, [k]: e.target.value }));

  const submitPassword = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (pw.next.length < MIN_PASSWORD) return setPwError(`Use at least ${MIN_PASSWORD} characters.`);
    if (pw.next !== pw.confirm) return setPwError('The two new passwords do not match.');
    setPwError('');
    setBusy('password');
    try {
      await changePassword(pw.current, pw.next);
      setPw(EMPTY_PW);
      toast({ variant: 'success', title: 'Password changed', description: 'Your other devices were signed out.' });
    } catch (err) {
      setPwError(errorMessage(err, 'Could not change the password'));
    } finally {
      setBusy(null);
    }
  };

  const signOutOthers = async () => {
    if (busy) return;
    setBusy('others');
    try {
      // The server revokes every token issued before now, this tab's included;
      // the helper swaps in the replacement that keeps this device signed in.
      await signOutOtherDevices();
      toast({ variant: 'success', title: 'Signed out of your other devices' });
    } catch (e) {
      toast({ variant: 'destructive', title: 'Could not sign out other devices', description: errorMessage(e) });
    } finally {
      setBusy(null);
    }
  };

  const replayTour = async () => {
    if (busy) return;
    setBusy('tour');
    try {
      await api.post('/users/me/reset-onboarding');
      outlet?.replayTour?.();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Could not restart the tour', description: errorMessage(e) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <PageContainer title="Account" subtitle="Your details, password and sign-in." titleProps={{ 'data-testid': 'account-title' }}>
      <ProfileCard user={user} onSaved={updateUser} />

      <form className={CARD} onSubmit={submitPassword} data-testid="account-password-form">
        <div className={SECTION}>Password</div>
        <PasswordInput autoComplete="current-password" placeholder="Current password" aria-label="Current password"
          value={pw.current} onChange={setPwField('current')} data-testid="account-pw-current" />
        <PasswordInput autoComplete="new-password" placeholder={`New password (${MIN_PASSWORD}+ characters)`} aria-label="New password"
          value={pw.next} onChange={setPwField('next')} data-testid="account-pw-new" />
        <PasswordInput autoComplete="new-password" placeholder="Repeat new password" aria-label="Repeat new password"
          value={pw.confirm} onChange={setPwField('confirm')} data-testid="account-pw-confirm" />
        {pwError && <p role="alert" className="text-[12px] text-red-600" data-testid="account-pw-error">{pwError}</p>}
        <button type="submit" disabled={!!busy || !pw.current || !pw.next || !pw.confirm} data-testid="account-pw-submit" className={ACTION}>
          {busy === 'password' && <Loader2 size={13} className="animate-spin" />}
          Change password
        </button>
      </form>

      <TwoStepCard />

      <div className={CARD}>
        <div className={SECTION}>Language</div>
        <div className="flex flex-wrap gap-1.5" data-testid="account-lang-switcher">
          {Object.entries(LANGS).map(([code, l]) => (
            <button
              key={code}
              type="button"
              onClick={() => setLang(code)}
              aria-pressed={lang === code}
              data-testid={`account-lang-${code}`}
              className={`text-[12px] font-semibold px-2.5 py-1 rounded-md border ${lang === code ? 'border-[#070B5F] bg-[#070B5F] text-white' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
            >
              {l.name}
            </button>
          ))}
        </div>
      </div>

      <div className={CARD}>
        <div className={SECTION}>Devices</div>
        <p className="text-[12px] text-slate-500">Signs you out everywhere else. This device stays signed in.</p>
        <button type="button" onClick={signOutOthers} disabled={!!busy} data-testid="account-signout-others" className={ACTION}>
          {busy === 'others' && <Loader2 size={13} className="animate-spin" />}
          Sign out my other devices
        </button>
      </div>

      <div className={CARD}>
        <div className={SECTION}>Product tour</div>
        <button type="button" onClick={replayTour} disabled={!!busy} data-testid="account-replay-tour" className={ACTION}>
          {busy === 'tour' && <Loader2 size={13} className="animate-spin" />}
          Replay the tour
        </button>
      </div>
    </PageContainer>
  );
}
